import React, { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';

const ConfigMigration: React.FC = () => {
  const [configData, setConfigData] = useState<string>('');
  const [isValidating, setIsValidating] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [migrationResult, setMigrationResult] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const validateConfig = useMutation(api.migrateSitesFromConfig.validateConfigData);
  const migrateSites = useMutation(api.migrateSitesFromConfig.migrateSitesFromConfig);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setConfigData(content);
      setError('');
      setValidationResult(null);
      setMigrationResult(null);
    };
    reader.readAsText(file);
  };

  const handleValidate = async () => {
    if (!configData.trim()) {
      setError('Please upload a config file first');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      const result = await validateConfig({ configData });
      setValidationResult(result);
    } catch (err) {
      setError(`Validation failed: ${err}`);
    } finally {
      setIsValidating(false);
    }
  };

  const handleMigrate = async () => {
    if (!configData.trim()) {
      setError('Please upload a config file first');
      return;
    }

    if (!validationResult?.valid) {
      setError('Please validate the config data first');
      return;
    }

    setIsMigrating(true);
    setError('');

    try {
      const result = await migrateSites({ configData });
      setMigrationResult(result);
    } catch (err) {
      setError(`Migration failed: ${err}`);
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <div className="modal">
      <div className="modal-header">
        <h2 className="modal-title">Secure Site Migration</h2>
        <p style={{ fontSize: '12px', color: '#B0B0B0', marginTop: '5px' }}>
          Upload your config.json to migrate all sites securely
        </p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div className="form-group">
          <label className="form-label">Upload Config File</label>
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            className="form-input"
            style={{ padding: '10px' }}
          />
          <p style={{ fontSize: '11px', color: '#B0B0B0', marginTop: '5px' }}>
            Select your config.json file containing site data
          </p>
        </div>

        {configData && (
          <div style={{ marginTop: '15px' }}>
            <button
              onClick={handleValidate}
              disabled={isValidating}
              className="btn btn-primary"
              style={{ marginRight: '10px' }}
            >
              {isValidating ? 'Validating...' : 'Validate Config'}
            </button>

            {validationResult?.valid && (
              <button
                onClick={handleMigrate}
                disabled={isMigrating}
                className="btn btn-primary"
              >
                {isMigrating ? 'Migrating...' : 'Migrate Sites'}
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{ 
          background: '#2A1A1A', 
          border: '1px solid #DC3545', 
          borderRadius: '6px', 
          padding: '10px', 
          marginBottom: '15px',
          color: '#DC3545'
        }}>
          {error}
        </div>
      )}

      {validationResult && (
        <div style={{ 
          background: '#1A2A1A', 
          border: '1px solid #28A745', 
          borderRadius: '6px', 
          padding: '15px', 
          marginBottom: '15px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#28A745' }}>Validation Results</h4>
          <div style={{ fontSize: '12px' }}>
            <p><strong>Total Sites:</strong> {validationResult.totalSites}</p>
            <p><strong>Valid Sites:</strong> {validationResult.validSites}</p>
            <p><strong>Invalid Sites:</strong> {validationResult.invalidSites}</p>
            {validationResult.errors?.length > 0 && (
              <div>
                <p><strong>Errors:</strong></p>
                <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                  {validationResult.errors.map((err: string, index: number) => (
                    <li key={index} style={{ fontSize: '11px', color: '#FFC107' }}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {migrationResult && (
        <div style={{ 
          background: '#1A2A1A', 
          border: '1px solid #28A745', 
          borderRadius: '6px', 
          padding: '15px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#28A745' }}>Migration Results</h4>
          <div style={{ fontSize: '12px' }}>
            <p><strong>Total Sites:</strong> {migrationResult.totalSites}</p>
            <p><strong>Successfully Migrated:</strong> {migrationResult.successCount}</p>
            <p><strong>Errors:</strong> {migrationResult.errorCount}</p>
            {migrationResult.errors?.length > 0 && (
              <div>
                <p><strong>Error Details:</strong></p>
                <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                  {migrationResult.errors.map((err: string, index: number) => (
                    <li key={index} style={{ fontSize: '11px', color: '#FFC107' }}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ 
        background: '#1A1A2A', 
        border: '1px solid #007bff', 
        borderRadius: '6px', 
        padding: '15px', 
        marginTop: '20px'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#007bff' }}>Security Features</h4>
        <ul style={{ fontSize: '12px', margin: '0', paddingLeft: '20px' }}>
          <li>Data is processed locally in your browser</li>
          <li>No data is stored on external servers</li>
          <li>Validation ensures data integrity</li>
          <li>Duplicate sites are automatically skipped</li>
          <li>Detailed error reporting for troubleshooting</li>
        </ul>
      </div>
    </div>
  );
};

export default ConfigMigration; 